import { useEffect, useState, useRef, forwardRef, useImperativeHandle, memo } from 'react';
import { isNil, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { FromToBlockItem, FromToBlocksRef } from 'components/common/FromToBlocks/types';
// Import UI components
import { BusyLoader } from '@geneui/components';
// Import Components
import { FromToBlocks } from 'components/common/FromToBlocks';
// Import Constants
import { l } from 'constants/common';

interface Props {
    leftKPIColumns: AnyObject[];
    rightKPIColumns: AnyObject[];
    isLoading?: boolean;
    getUpdate?: (data: { leftBlockList: FromToBlockItem[]; rightBlockList: FromToBlockItem[] }) => void;
}

export interface KPIChooserRef {
    reset?: () => void;
    setInitialData: (rightKPIColumns: FromToBlockItem[], leftKPIColumns: FromToBlockItem[]) => void;
}

const KPIChooser = forwardRef<KPIChooserRef, Props>((props, ref) => {
    const { leftKPIColumns, rightKPIColumns, isLoading = false, getUpdate = noop } = props;
    const { t } = useTranslation();

    const [leftBlockList, setLeftBlockList] = useState<Nullable<FromToBlockItem[]>>(null);
    const [rightBlockList, setRightBlockList] = useState<Nullable<FromToBlockItem[]>>(null);
    const fromToBlocksRef = useRef<FromToBlocksRef>(null);

    useImperativeHandle(ref, () => ({
        reset: fromToBlocksRef.current?.reset,
        setInitialData: (rightKPIColumns, leftKPIColumns) => {
            fromToBlocksRef.current?.setInitialBlocksData?.(
                rightKPIColumns.map(mapToLocalModel),
                leftKPIColumns.map(mapToLocalModel),
            );
        },
    }));

    const mapToLocalModel = (item: AnyObject) =>
        ({
            ...item,
            isVisible: true,
            isChecked: false,
            description: item.Description,
            childrenList: isNil(item.childrenList)
                ? []
                : item.childrenList.map((row) => ({ ...row, isChecked: false, isVisible: true })),
        }) as FromToBlockItem;

    useEffect(() => {
        setLeftBlockList(leftKPIColumns.map(mapToLocalModel));
        setRightBlockList(rightKPIColumns.map(mapToLocalModel));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFromToBlockUpdates = ({ leftBlockList, rightBlockList }) => {
        getUpdate?.({ leftBlockList, rightBlockList });
    };

    return (
        <div className="crm-report-d-a-d-container">
            <BusyLoader
                isBusy={isLoading || isNil(leftBlockList) || isNil(rightBlockList)}
                type="spinner"
                loadingText={t(l.Loading)}
            >
                {!isLoading && !isNil(leftBlockList) && !isNil(rightBlockList) && (
                    <FromToBlocks
                        ref={fromToBlocksRef}
                        hasInfoTooltip={true}
                        leftBlockList={leftBlockList}
                        rightBlockList={rightBlockList}
                        leftBlockWithParent={true}
                        rightBlockWithParent={false}
                        getUpdate={getFromToBlockUpdates}
                        leftBlockWithPosition={false}
                        rightBlockWithPosition={true}
                        leftBlockTitle={t(l.NotSelected)}
                        rightBlockTitle={t(l.Selected)}
                        withDisabled={true}
                    />
                )}
            </BusyLoader>
        </div>
    );
});

KPIChooser.displayName = 'KPIChooser';

export default memo(KPIChooser);
